import { type FormEvent, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSubmitScore } from '@/hooks/useJudging';
import { getApiErrorMessage } from '@/api/client';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import type { JudgingScore } from '@/api/types';

const SCORE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.isContentEditable
  );
}

/**
 * There is no separate "update score" endpoint - POST /judging/scores is an
 * idempotent upsert keyed on (submissionId, calling judge), so both first
 * submission and later edits go through the same submitScore call. When the
 * calling judge already has a score for this submission, pass it in via
 * `existingScore` to prefill the form and label the action as an update.
 *
 * Mount it with `key={submissionId}` so moving to the next entry resets the
 * draft and the saved/error state instead of carrying them over.
 *
 * Keyboard (UI_GUIDELINES §5 Score control): 1–9 and 0 (= 10) pick the score,
 * Enter saves and moves on (or finishes on the last entry). Ignored while
 * typing in the comment field. ← / → move between entries - handled by the
 * page, since that also has to work when scoring is closed.
 */
export function JudgeScoreForm({
  submissionId,
  existingScore,
  canEdit = true,
  hasNext = false,
  onNext,
  hasPrev = false,
  onPrev,
  onFinish,
  layout = 'inline',
}: {
  submissionId: string;
  existingScore?: JudgingScore | null;
  /** Scoring is only allowed while the competition is in its judging phase - see JudgingService.SubmitScoreAsync. */
  canEdit?: boolean;
  /** Whether a next entry exists in the judging queue. */
  hasNext?: boolean;
  /** Navigate to the next entry in the queue. */
  onNext?: () => void;
  /** Whether a previous entry exists in the judging queue. */
  hasPrev?: boolean;
  /** Navigate back to the previous entry in the queue. */
  onPrev?: () => void;
  /** Passed on the LAST entry: called after a successful save instead of moving on ("Shrani in zaključi"). */
  onFinish?: () => void;
  /** `panel` = narrow side panel (fullscreen); `inline` = full-width under the photo. */
  layout?: 'inline' | 'panel';
}) {
  const [value, setValue] = useState(existingScore ? String(existingScore.value) : '');
  const [comments, setComments] = useState(existingScore?.comments ?? '');
  const submitScore = useSubmitScore();

  useEffect(() => {
    setValue(existingScore ? String(existingScore.value) : '');
    setComments(existingScore?.comments ?? '');
  }, [existingScore]);

  const parsed = Number(value);
  const isValid = Boolean(value) && !Number.isNaN(parsed) && parsed >= 1 && parsed <= 10;

  async function save(): Promise<boolean> {
    if (!isValid) return false;
    try {
      await submitScore.mutateAsync({
        submissionId,
        value: parsed,
        comments: comments.trim() || undefined,
      });
      return true;
    } catch {
      // Surfaced inline via submitScore.error.
      return false;
    }
  }

  async function saveAndNext() {
    if (await save()) onNext?.();
  }

  async function saveAndFinish() {
    if (await save()) onFinish?.();
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (hasNext && onNext) void saveAndNext();
    else if (onFinish) void saveAndFinish();
    else void save();
  }

  useEffect(() => {
    if (!canEdit) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || isTypingTarget(e.target)) return;
      if (e.key >= '1' && e.key <= '9') {
        setValue(e.key);
        e.preventDefault();
      } else if (e.key === '0') {
        setValue('10');
        e.preventDefault();
      } else if (e.key === 'Enter') {
        if (!isValid || submitScore.isPending) return;
        e.preventDefault();
        if (hasNext && onNext) void saveAndNext();
        else if (onFinish) void saveAndFinish();
        else void save();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, isValid, hasNext, onNext, onFinish, parsed, comments, submitScore.isPending]);

  const prevButton = hasPrev && onPrev && (
    <Button type="button" variant="ghost" onClick={onPrev}>
      <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      Nazaj
    </Button>
  );

  const nextButton = hasNext && onNext && (
    <Button type="button" variant={canEdit ? 'ghost' : 'secondary'} onClick={onNext}>
      {canEdit ? 'Preskoči' : 'Naslednja slika'}
      <ChevronRight className="h-4 w-4" aria-hidden="true" />
    </Button>
  );

  if (!canEdit) {
    return (
      <div className="flex flex-col gap-3">
        <Alert variant="info">
          {existingScore
            ? 'Ocenjevanje za to tekmovanje ni več na voljo - ocene ni več mogoče spreminjati.'
            : 'Ocenjevanje za to tekmovanje trenutno ni na voljo.'}
        </Alert>
        {(prevButton || nextButton) && (
          <div className="flex justify-between gap-2">
            <div>{prevButton}</div>
            <div>{nextButton}</div>
          </div>
        )}
      </div>
    );
  }

  const panel = layout === 'panel';

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      {submitScore.isError && <Alert variant="error">{getApiErrorMessage(submitScore.error)}</Alert>}
      {submitScore.isSuccess && (
        <Alert variant="success">{existingScore ? 'Ocena posodobljena.' : 'Ocena oddana.'}</Alert>
      )}

      <div>
        <div className="mb-1.5 flex items-baseline justify-between gap-2">
          <span id={`score-label-${submissionId}`} className="text-sm font-medium text-fg">
            Ocena (1-10)
          </span>
          <span className="font-mono text-xl font-medium tabular-nums text-fg">
            {value || '–'}
            <span className="text-xs font-normal text-fg-muted"> / 10</span>
          </span>
        </div>
        <div
          role="radiogroup"
          aria-labelledby={`score-label-${submissionId}`}
          className={`grid gap-1 ${panel ? 'grid-cols-5 gap-1.5' : 'grid-cols-10'}`}
        >
          {SCORE_OPTIONS.map((n) => {
            const selected = parsed === n;
            return (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setValue(String(n))}
                className={`flex items-center justify-center rounded-[5px] border font-mono tabular-nums transition-colors duration-fast ${
                  panel ? 'h-10 text-sm' : 'h-8 text-xs'
                } ${
                  selected
                    ? 'border-accent bg-accent text-fg-on-accent'
                    : 'border-line-strong bg-surface-0 text-fg-secondary hover:bg-surface-2 hover:text-fg'
                }`}
              >
                {n}
              </button>
            );
          })}
        </div>
      </div>

      <Input label="Komentar (neobvezno)" value={comments} onChange={(e) => setComments(e.target.value)} />

      <div className={`flex flex-wrap items-center gap-2 ${panel ? 'flex-col items-stretch' : ''}`}>
        {hasNext && onNext ? (
          <>
            <Button type="submit" isLoading={submitScore.isPending} disabled={!isValid}>
              Shrani in naprej
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
            <div className={`flex gap-2 ${panel ? 'justify-between' : ''}`}>
              {prevButton}
              <Button
                type="button"
                variant="secondary"
                isLoading={submitScore.isPending}
                disabled={!isValid}
                onClick={() => void save()}
              >
                {existingScore ? 'Posodobi oceno' : 'Shrani oceno'}
              </Button>
              {nextButton}
            </div>
          </>
        ) : onFinish ? (
          <>
            <Button type="submit" isLoading={submitScore.isPending} disabled={!isValid}>
              Shrani in zaključi
            </Button>
            <div className={`flex gap-2 ${panel ? 'justify-between' : ''}`}>
              {prevButton}
              <Button type="button" variant="ghost" onClick={onFinish}>
                Zaključi brez shranjevanja
              </Button>
            </div>
          </>
        ) : (
          <>
            <Button type="submit" isLoading={submitScore.isPending} disabled={!isValid}>
              {existingScore ? 'Posodobi oceno' : 'Oddaj oceno'}
            </Button>
            {prevButton}
          </>
        )}
      </div>

      <p className="font-mono text-[11px] text-fg-muted">
        Tipke 1–9 in 0 (= 10) izberejo oceno · Enter shrani{hasNext ? ' in gre naprej' : onFinish ? ' in zaključi' : ''}
        {hasPrev || hasNext ? ' · ← → med slikami' : ''} · R zavrti sliko
      </p>
    </form>
  );
}
